import {
  buildMealPrepSourceRevision,
  buildPublishedMealPrep,
  getMealPrepPublicationIssue,
  parseMealPrepOverride,
} from "./mealPrep";
import { resolveFourWeekRotationDays } from "./fourWeekRotation";
import { sha256HexSync } from "./sha256Sync";

const KEYS = new Set(["version", "status", "source_revision"]);

function stableValue(value) {
  if (Array.isArray(value)) return value.map(stableValue);
  if (!value || typeof value !== "object") return value;
  return Object.fromEntries(
    Object.keys(value).sort().map((key) => [key, stableValue(value[key])]),
  );
}

function cleanFridge(fridge) {
  const cleanItems = (items) => (Array.isArray(items) ? items : []).map((item) => ({
    id: String(item?.id || ""),
    label: String(item?.label || "").trim(),
  }));
  const cleanCategories = (categories) => (Array.isArray(categories) ? categories : []).map((category) => ({
    id: String(category?.id || ""),
    title: String(category?.title || "").trim(),
    items: cleanItems(category?.items),
  }));
  return {
    essentials: cleanItems(fridge?.essentials),
    favorite: cleanCategories(fridge?.favorite),
    limit: cleanCategories(fridge?.limit),
  };
}

function countClassicItems(fridge) {
  const cleaned = cleanFridge(fridge);
  return cleaned.essentials.length
    + cleaned.favorite.reduce((sum, category) => sum + category.items.length, 0)
    + cleaned.limit.reduce((sum, category) => sum + category.items.length, 0);
}

function getSourceCompletenessIssue(weekMeals, mealPrepValue, fridge) {
  const prep = buildPublishedMealPrep(weekMeals, mealPrepValue);
  if (!prep) return "Valide d’abord le Meal Prep avant d’actualiser la fiche frigo.";
  if (countClassicItems(fridge) === 0) {
    return "Complète les repères classiques du frigo avant l’actualisation finale.";
  }
  const linkedMealIds = new Set(
    prep.sessions.flatMap((session) => session.tasks.flatMap((task) => task.linked_meal_ids)),
  );
  for (const day of resolveFourWeekRotationDays(weekMeals)) {
    for (const meal of Array.isArray(day?.meals) ? day.meals : []) {
      const ingredients = Array.isArray(meal?.recipe?.ingredients)
        ? meal.recipe.ingredients.filter((item) => String(item || "").trim())
        : [];
      const preparation = Array.isArray(meal?.recipe?.preparation)
        ? meal.recipe.preparation.filter((item) => String(item || "").trim())
        : [];
      if (!ingredients.length || !preparation.length) {
        return "Toutes les recettes de la semaine doivent être détaillées avant la fiche frigo.";
      }
      const linkedMealId = meal?.source_meal_id || meal?.id;
      if (!linkedMealId || !linkedMealIds.has(linkedMealId)) {
        return "Le Meal Prep doit couvrir tous les menus de la semaine avant la fiche frigo.";
      }
    }
  }
  return null;
}

export function buildFridgeSyncSourceRevision(weekMeals, mealPrepValue, fridge) {
  const publishedMealPrep = buildPublishedMealPrep(weekMeals, mealPrepValue);
  if (!publishedMealPrep) return null;
  const source = stableValue({
    meal_prep: publishedMealPrep,
    meal_prep_source_revision: buildMealPrepSourceRevision(weekMeals),
    rotation: weekMeals?.rotation || null,
    fridge: cleanFridge(fridge),
  });
  return `fridge-sync-v2-${sha256HexSync(JSON.stringify(source))}`;
}

export function parseFridgeSyncOverride(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  if (Object.keys(value).some((key) => !KEYS.has(key))) return null;
  if (value.version !== 1 || value.status !== "validated") return null;
  if (
    typeof value.source_revision !== "string"
    || !/^fridge-sync-v(?:1|2)-[0-9a-f]{64}$/.test(value.source_revision)
  ) return null;
  return {
    version: 1,
    status: "validated",
    source_revision: value.source_revision,
  };
}

export function buildFridgeSyncOverride(weekMeals, mealPrepValue, fridge) {
  const mealPrepIssue = getMealPrepPublicationIssue(weekMeals, mealPrepValue);
  if (mealPrepIssue) throw new Error(mealPrepIssue);
  const completenessIssue = getSourceCompletenessIssue(weekMeals, mealPrepValue, fridge);
  if (completenessIssue) throw new Error(completenessIssue);
  const sourceRevision = buildFridgeSyncSourceRevision(weekMeals, mealPrepValue, fridge);
  if (!sourceRevision) {
    throw new Error("Valide d’abord le Meal Prep avant d’actualiser la fiche frigo.");
  }
  return { version: 1, status: "validated", source_revision: sourceRevision };
}

export function getFridgeSyncPublicationIssue(weekMeals, mealPrepValue, fridge, value) {
  if (!mealPrepValue) return null;
  const parsedMealPrep = parseMealPrepOverride(mealPrepValue);
  if (parsedMealPrep && !parsedMealPrep.enabled) return null;
  const mealPrepIssue = getMealPrepPublicationIssue(weekMeals, mealPrepValue);
  if (mealPrepIssue) return mealPrepIssue;
  const completenessIssue = getSourceCompletenessIssue(weekMeals, mealPrepValue, fridge);
  if (completenessIssue) return completenessIssue;
  const currentRevision = buildFridgeSyncSourceRevision(weekMeals, mealPrepValue, fridge);
  if (!currentRevision) return null;
  const parsed = parseFridgeSyncOverride(value);
  if (!parsed) return "Fiche frigo à actualiser après le Meal Prep";
  if (parsed.source_revision !== currentRevision) {
    return "Fiche frigo à réactualiser après les dernières modifications";
  }
  return null;
}

export function buildFridgeSyncPreview(weekMeals, mealPrepValue, fridge) {
  const override = buildFridgeSyncOverride(weekMeals, mealPrepValue, fridge);
  const prep = buildPublishedMealPrep(weekMeals, mealPrepValue);
  const ingredients = [];
  const seen = new Set();
  for (const day of resolveFourWeekRotationDays(weekMeals)) {
    for (const meal of Array.isArray(day?.meals) ? day.meals : []) {
      for (const ingredient of Array.isArray(meal?.recipe?.ingredients) ? meal.recipe.ingredients : []) {
        const label = String(ingredient || "").replace(/\s+/g, " ").trim();
        const key = label.toLocaleLowerCase("fr");
        if (label && !seen.has(key)) {
          seen.add(key);
          ingredients.push(label);
        }
      }
    }
  }
  return {
    override,
    sessionCount: prep.sessions.length,
    taskCount: prep.sessions.reduce((sum, session) => sum + session.tasks.length, 0),
    ingredients,
    classicItemCount: countClassicItems(fridge),
  };
}
